/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Filter and sort Sagas
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */
import type { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

import { filterToJson } from '@/main/sagas/filterToJson';
import { requestSaga } from '@/main/sagas/requestSaga';
import { API_HOSTNAME, FILTER_AND_SORT, FILTER_AND_SORT_PRESET } from '@modules/app/Api';
import { stringFormatParams } from '@utils/stringFormat';

import {
    collapseTableSubRows,
    loadTableMeta,
    loadTableMetaError,
    sendTableFilterAndSortError,
    sendTableFilterAndSortSuccess,
} from '../actions/tableActions';
import { LOAD_TABLE_META } from '../constants/actionConstants';
import {
    makeSelectorBasketContext,
    makeSelectorTableCurrentName,
    makeSelectorTableFilter,
    makeSelectorTableSort,
} from '../selectors/tableSelectors';
import { MapTableNameToApiPath } from '../types/tableMapTypes';
import {
    TableNameType,
    TDropTableFilterAndSortAction,
    TSetTableFilterAction,
    TSetTableFilterPresetAction,
    TSetTableSortAction,
} from '../types/tableTypes';
import { loadTableListSaga } from './loadTableListSaga';
import { loadTableMetaSagas } from './loadTableMetaSagas';

export function* dropTableFilterAndSortSaga(action: TDropTableFilterAndSortAction): SagaIterator {
    return yield call(sendTableFilterAndSortSaga, {
        skipLoadList: action.payload?.skipLoadList,
    });
}

export function* sendTableSortSaga(action: TSetTableSortAction): SagaIterator {
    return yield call(sendTableFilterAndSortSaga, {
        skipLoadList: action.payload.skipLoadList,
    });
}

export function* sendTableFilterSaga(action: TSetTableFilterAction): SagaIterator {
    return yield call(sendTableFilterAndSortSaga, {
        skipLoadList: action.payload.skipLoadList,
    });
}

interface ISendTableFilterAndSortSagaParams {
    skipLoadList?: boolean;
}

export function* sendTableFilterAndSortSaga(params: ISendTableFilterAndSortSagaParams): SagaIterator {

    const tableName: TableNameType = yield select(makeSelectorTableCurrentName());
    const URL = MapTableNameToApiPath[tableName];
    const context = yield select(makeSelectorBasketContext());
    const contextForUrl = context ? `?context=${context}` : '';
    const queryParams = { context };
    const filterAndSortUrl = `${API_HOSTNAME}${URL}${FILTER_AND_SORT}${contextForUrl}`;


    try {
        const filterAndSort = {
            filter: yield select(makeSelectorTableFilter()),
            sort: yield select(makeSelectorTableSort()),
        };
        const json = filterToJson(filterAndSort);
        yield call(requestSaga, filterAndSortUrl, 'POST', json);

        if (!params.skipLoadList) {
            yield put(collapseTableSubRows(true));

            // Remember current scroll position
            const tbody = document.querySelector('.rt-tbody');
            const thead = document.querySelector('.rt-thead');
            if (tbody) {
                tbody.classList.add('hidden');
            }
            const scrollX: number = thead?.scrollLeft ?? 0;

            // Load new meta
            yield call(loadTableMetaSagas, {
                payload: {
                    force: true,
                    tableName: tableName,
                    skipLoadList: true,
                    queryParams,
                },
                type: LOAD_TABLE_META,
            });

            // Load new list
            yield call(loadTableListSaga, {
                payload: { force: true },
                type: '',
            });

            // return previous horizontal position
            yield call(() => {
                if (tbody && thead) {
                    thead.scrollLeft = scrollX;
                    tbody.scrollLeft = scrollX;
                }
            });

            if (tbody) {
                tbody.classList.remove('hidden');
            }
            yield put(collapseTableSubRows(false));
        }
        yield put(sendTableFilterAndSortSuccess());
    } catch (error: any) {
        yield put(sendTableFilterAndSortError({ error }));
    }
}

export function* setTableFilterPresetSaga({ payload }: TSetTableFilterPresetAction): SagaIterator {
    const context = yield select(makeSelectorBasketContext());
    const queryParams = { context };

    try {
        if (payload.filterId) {
            const URL = MapTableNameToApiPath[payload.tableName];
            yield call(
                requestSaga,
                stringFormatParams(API_HOSTNAME + URL + FILTER_AND_SORT_PRESET, { filterId: payload.filterId }),
                'POST',
            );
        }
    } catch (e) {
        // ignore filter preset request
    }

    try {
        yield put(loadTableMeta({ tableName: payload.tableName, queryParams }));
    } catch (e: any) {
        yield put(loadTableMetaError(e));
    }
}
